import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import TooltipWithClickAway from "./terminalPicker/TooltipWithClickAway";
import CustomDialog from "./confirmationPopup/confirmationPopup";

type Props = {
  isOpen?: boolean;
  onClose?: () => void;
  setExportTo?: (value: string) => void;
  recordCount?: number;
};

const ExportToExcel: React.FC<Props> = ({
  isOpen,
  onClose,
  setExportTo,
  recordCount = 0
}: Props): JSX.Element => {
  const [error, setError] = useState("");
  const [to, setTo] = useState("");
  const [touched, setTouched] = useState(false);

  // Email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  // Validate multiple comma-separated emails
  const validateEmails = (emails: string) => {
    return emails
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e.length > 0)
      .every((e) => emailRegex.test(e));
  };

  const exportExcel = () => {
    setTouched(true);
    if (!to.trim()) {
      setError("Email address is Required.");
      return;
    }
    if (!validateEmails(to)) {
      setError("Please enter valid email address(es).");
      return;
    }
    setError("");
    setDialogOpen(true);
  };

  const handleClose = () => {
    setTo("");
    setTouched(false);
    setError("");
    if (onClose) {
      onClose();
    }
  };
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogClose = (flag?: string) => {
    setDialogOpen(false);
    if (flag === "yes" && setExportTo) {
      setExportTo(to);
      handleClose();
    }
  };

  return (
    <>
      <Dialog
        open={isOpen!}
        aria-labelledby="customized-dialog-title"
        keepMounted
        maxWidth="sm"
        fullWidth={true}
        className="assignment"
        data-testid="export-to-excel-dialog"
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
          className="modal-header"
          data-testid="dialog-title"
        >
          Export to Excel
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 15,
            top: 15,
            color: (theme: any) => theme.palette.grey[500],
          }}
          data-testid="close-button"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          dividers
          className="assignment-area"
          data-testid="dialog-content"
        >
          <Grid
            item
            md={3}
            xs={9}
            className={error ? "align-center-export" : ""}
          >
            <FormControl fullWidth size="small">
              <InputLabel
                style={{ fontSize: "13px" }}
                shrink={true}
                className="label-email export"
                htmlFor="to"
              >
                To<sup>*</sup>{" "}
                <TooltipWithClickAway
                  tooltipText="Enter multiple email addresses, separated by commas."
                  tooltipKey="tooltip2"
                  data-testid="tooltip-to"
                />
              </InputLabel>
              <TextField
                autoComplete="off"
                id="to"
                inputProps={{ "data-testid": "export-to-email-input" }}
                name="to"
                data-testid="export-to-email-input-2"
                variant="outlined"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  if (touched) {
                    if (validateEmails(e.target.value)) {
                      setError("");
                    } else {
                      setError("Please enter valid email address(es).");
                    }
                  }
                }}
                error={Boolean(error)}
                helperText={error}
              />
            </FormControl>
            <Button
              onClick={() => {
                if (!to?.trim()) return;
                setTouched(true);
                exportExcel();
              }}
              variant="contained"
              data-testid="export-send-button"
              className={to?.trim() ? "assign" : " disable disable-btn assign"}
              disabled={!to?.trim()}
            >
              Send
            </Button>
            <Button
              onClick={handleClose}
              variant="contained"
              data-testid="cancel-button"
              className={"disable mt-1"}
            >
              Cancel
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
      <CustomDialog
        key={dialogOpen ? "open" : "close"}
        message={`<p> ${recordCount} records are ready to export. You can apply filters to narrow the results. </p> 
        <p>Do you want to continue? </p>`}
        open={dialogOpen}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default ExportToExcel;
